import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { iif, insertItem, patch, updateItem } from '@ngxs/store/operators';
import { AuditTrailItem, AuditTrailShiftType } from '@wilson/api/gateway';
import {
  ResetAuditTrailShiftState,
  UpsertAuditTrailShiftState,
} from './audit-trail-shift.action';

export interface AuditTrailShiftStateModel {
  audits: AuditTrailShiftStateItem[];
}

export interface AuditTrailShiftStateItem {
  id: string;
  auditTrailItems: AuditTrailItem[];
}

const defaultAuditTrailShiftState: AuditTrailShiftStateModel = {
  audits: [],
};

@State<AuditTrailShiftStateModel>({
  name: 'AuditTrailShiftState',
  defaults: defaultAuditTrailShiftState,
})
@Injectable()
export class AuditTrailShiftState {
  private static findShiftAuditById(
    shiftId: string,
    state: AuditTrailShiftStateModel,
  ): AuditTrailItem[] {
    return (
      state.audits.find((shiftAudit) => shiftAudit.id === shiftId)
        ?.auditTrailItems ?? []
    );
  }

  @Selector()
  static audits(state: AuditTrailShiftStateModel): AuditTrailShiftStateItem[] {
    return state.audits;
  }

  static auditById(
    shiftId: string,
  ): (state: AuditTrailShiftStateModel) => AuditTrailItem[] {
    return createSelector(
      [AuditTrailShiftState],
      (state: AuditTrailShiftStateModel) => {
        return AuditTrailShiftState.findShiftAuditById(shiftId, state);
      },
    );
  }

  static findAuditItemOfSameType(
    shiftId: string,
    currentAuditItem: AuditTrailItem,
    searchedType: AuditTrailShiftType,
  ): (state: AuditTrailShiftStateModel) => AuditTrailItem | null {
    return createSelector(
      [AuditTrailShiftState],
      (state: AuditTrailShiftStateModel) => {
        const shiftAudit = AuditTrailShiftState.findShiftAuditById(
          shiftId,
          state,
        );
        const currentItemIndex = shiftAudit.findIndex(
          (auditItem) =>
            JSON.stringify(auditItem) === JSON.stringify(currentAuditItem),
        );
        let lastItemOfType = shiftAudit.find(
          (auditItem, index) =>
            auditItem.types.some((type) => type === searchedType) &&
            index !== currentItemIndex &&
            index > currentItemIndex,
        );

        if (!lastItemOfType) {
          lastItemOfType = shiftAudit.find(
            (auditItem) => auditItem.operation === 'INSERT',
          );
        }

        return lastItemOfType ?? null;
      },
    );
  }

  @Action(ResetAuditTrailShiftState)
  resetAuditTrailShiftState({
    setState,
  }: StateContext<AuditTrailShiftStateModel>): void {
    setState(defaultAuditTrailShiftState);
  }

  @Action(UpsertAuditTrailShiftState)
  UpsertAuditTrailShiftState(
    { setState }: StateContext<AuditTrailShiftStateModel>,
    { shiftId, auditTrailShift }: UpsertAuditTrailShiftState,
  ): void {
    setState(
      patch({
        audits: iif<AuditTrailShiftStateItem[]>(
          (audits) => audits?.some((audit) => audit.id === shiftId) ?? false,
          updateItem<AuditTrailShiftStateItem>(
            (item) => (item ? item.id === shiftId : false),
            patch({ auditTrailItems: auditTrailShift }),
          ),
          insertItem({ id: shiftId, auditTrailItems: auditTrailShift }),
        ),
      }),
    );
  }
}
